// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface AvatarItem {
  id: string | number;
  imageUrl?: string;
  name: string;
  backgroundColor?: string;
}

@Component({
  selector: 'lfx-overlapping-avatars',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
      template: `
    <div class="overlapping-avatars-container"
         [class]="'size-' + size() + (expandOnHover() ? ' expand-on-hover' : '')">
      <ul class="avatar-list">
        @for (avatar of visibleAvatars(); track avatar.id) {
          <li
            class="avatar-item"
            [style.background-color]="avatar.backgroundColor || getDefaultBackground($index)"
            [title]="avatar.name">
            @if (avatar.imageUrl) {
              <img
                [src]="avatar.imageUrl"
                [alt]="avatar.name"
                class="avatar-image"
                (error)="onImageError($event)">
            } @else {
              <span class="avatar-initials">{{ getInitials(avatar.name) }}</span>
            }
          </li>
        }
        @if (remainingCount() > 0) {
          <li class="avatar-item avatar-count" [title]="remainingCount() + ' more'">
            <span class="avatar-initials">+{{ remainingCount() }}</span>
          </li>
        }
      </ul>
    </div>
  `,
  styleUrls: ['./overlapping-avatars.component.scss']
})
export class OverlappingAvatarsComponent {
  readonly avatars = input.required<AvatarItem[]>();
  readonly maxVisible = input<number>(4);
  readonly size = input<'sm' | 'md' | 'lg'>('md');
  readonly expandOnHover = input<boolean>(true);

  protected readonly visibleAvatars = computed(() =>
    this.avatars().slice(0, this.maxVisible())
  );

  protected readonly remainingCount = computed(() =>
    Math.max(0, this.avatars().length - this.maxVisible())
  );

  private readonly defaultColors = [
    '#3B82F6', // blue
    '#10B981', // green
    '#8B5CF6', // purple
    '#F59E0B', // amber
    '#EF4444', // red
    '#06B6D4', // cyan
    '#84CC16', // lime
    '#F97316'  // orange
  ];

  protected getInitials(name: string): string {
    if (!name) return 'A';

    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    return words.slice(0, 2)
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  }

  protected getDefaultBackground(index: number): string {
    return this.defaultColors[index % this.defaultColors.length];
  }

  protected onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }
}
